import { AI_BRAND_VOICE_MAX_LENGTH } from "common/aiConstants";

/**
 * Tags delimiting brand kit text inside a prompt. User text must never contain
 * them, or it could close the block early and be read as system instructions.
 */
export const AI_RULES_PROMPT_BLOCK_TAGS = ["brandvoice", "presentationinstructions", "userrules"];

// Strips C0/C1 control characters while preserving tab and newline, which are
// meaningful formatting in multi-line instructions.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_REGEX = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;

const PROMPT_BLOCK_TAGS_REGEX = new RegExp(
    `<\\s*/?\\s*(?:${AI_RULES_PROMPT_BLOCK_TAGS.join("|")})\\s*>`,
    "gi"
);

/**
 * Sanitizes free-form brand kit text before it is stored or put into a prompt.
 * The single point of truth for what is safe to send to a model, applied on both
 * ends since a kit is editable by someone other than the user generating with it.
 *
 * Returns undefined when nothing usable is left, so the caller can unset the field.
 */
export function sanitizeAiRules(rules?: string | null, maxLength: number = AI_BRAND_VOICE_MAX_LENGTH): string | undefined {
    if (typeof rules !== "string") {
        return undefined;
    }

    let stripped = rules.replace(CONTROL_CHARS_REGEX, "");

    // Stripping in a single pass is bypassable by nesting: removing the inner tag
    // of "</br<brandvoice>andvoice>" leaves a working "</brandvoice>" behind. Keep
    // going until the text stops changing so no tag can be reassembled.
    let previous;
    do {
        previous = stripped;
        stripped = stripped.replace(PROMPT_BLOCK_TAGS_REGEX, "");
    } while (stripped !== previous);

    const sanitized = stripped
        .trim()
        .slice(0, maxLength)
        .trim();

    return sanitized.length > 0 ? sanitized : undefined;
}
